import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchCurriculumCatalog,
  type CurriculumMatrixCatalog,
  type CurriculumTarget,
  type GuideGenerationMode,
} from '@interactive-room';
import type { CurriculumTrace } from '../constants/examAiParams';
import { cn } from '../../lib/cn';
import {
  curriculumUnavailableMessage,
  hasCurriculumSubjectSupport,
} from '../constants/subjectsWithCurriculum';

/** Heurística alineada al servidor (suggestCurriculumDimensions.js). */
const AXIS_TO_OAH_GROUP: Record<string, string> = {
  numeros_y_operaciones: 'resolver_problemas',
  patrones_y_algebra: 'modelar',
  geometria: 'representar',
  medicion: 'representar',
  datos_y_probabilidades: 'argumentar_y_comunicar',
  escuchar: 'escuchar',
  interpretar: 'interpretar',
  crear: 'crear',
  reflexionar: 'reflexionar',
  lectura_comprension: 'comprender',
  escritura_produccion: 'producir',
  comunicacion_oral: 'comunicar',
};

const AXIS_TO_OAA_CODE: Record<string, string> = {
  numeros_y_operaciones: 'A',
  patrones_y_algebra: 'B',
  geometria: 'A',
  medicion: 'A',
  datos_y_probabilidades: 'F',
  escuchar: 'A',
  interpretar: 'D',
  crear: 'E',
  reflexionar: 'B',
  lectura_comprension: 'A',
  escritura_produccion: 'B',
  comunicacion_oral: 'C',
};

interface GuideCurriculumPanelProps {
  level: string;
  subjectTag: string;
  generationMode: GuideGenerationMode;
  curriculumTarget: CurriculumTarget | null;
  onGenerationModeChange: (mode: GuideGenerationMode) => void;
  onCurriculumTargetChange: (target: CurriculumTarget | null) => void;
  onTraceChange?: (trace: CurriculumTrace | null) => void;
  /** Solo interacción humana (selects) — no autocompletados de OAH/OAA. */
  onUserEdit?: () => void;
  /** Ajusta copy para planificación vs. generación de guías. */
  variant?: 'guide' | 'planning';
  /** Estilo Swiss minimalista para wizards de planificación. */
  appearance?: 'default' | 'wizard';
  /** Oculta el toggle libre/curricular (p. ej. modo inverso dedicado). */
  hideModeToggle?: boolean;
  /** Fuerza modo curricular sin permitir planificación libre. */
  lockCurricular?: boolean;
}

function SelectField({
  label,
  hint,
  value,
  onChange,
  options,
  disabled = false,
  placeholder,
  appearance = 'default',
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
  placeholder?: string;
  appearance?: 'default' | 'wizard';
}) {
  const isWizard = appearance === 'wizard';
  const selectedLabel = options.find((option) => option.value === value)?.label ?? '';

  return (
    <label className="block space-y-1">
      <span
        className={
          isWizard
            ? 'text-xs font-semibold uppercase tracking-wide text-gray-500'
            : 'sala-label'
        }
      >
        {label}
      </span>
      {hint && (
        <span
          className={
            isWizard ? 'block text-[11px] text-gray-400' : 'block text-[10px] text-[var(--sala-ink-dim)]'
          }
        >
          {hint}
        </span>
      )}
      <select
        className={
          isWizard
            ? 'mt-1 w-full cursor-pointer truncate rounded-lg border border-transparent bg-gray-50 px-3 py-2.5 text-sm text-gray-900 transition focus:border-transparent focus:bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50'
            : 'sala-input-dark mt-1 w-full text-sm'
        }
        value={value}
        disabled={disabled}
        title={isWizard ? selectedLabel : undefined}
        onChange={(e) => onChange(e.target.value)}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function GuideCurriculumPanel({
  level,
  subjectTag,
  generationMode,
  curriculumTarget,
  onGenerationModeChange,
  onCurriculumTargetChange,
  onTraceChange,
  onUserEdit,
  variant = 'guide',
  appearance = 'default',
  hideModeToggle = false,
  lockCurricular = false,
}: GuideCurriculumPanelProps) {
  const subjectHasMatrix = hasCurriculumSubjectSupport(subjectTag);
  const [catalog, setCatalog] = useState<CurriculumMatrixCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  useEffect(() => {
    if (!subjectHasMatrix) {
      setCatalog(null);
      setCatalogError(null);
      setCatalogLoading(false);
      return;
    }

    if (!String(level || '').trim()) {
      setCatalog(null);
      setCatalogError('Selecciona el nivel educativo para cargar la matriz MINEDUC.');
      setCatalogLoading(false);
      return;
    }

    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(null);

    fetchCurriculumCatalog({ level, subjectTag })
      .then((next) => {
        if (cancelled) return;
        setCatalog(next);
        if (!next) {
          setCatalogError('No hay matriz MINEDUC para este nivel y asignatura.');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setCatalogError(
            err instanceof Error && err.message
              ? err.message
              : 'No se pudo cargar el currículum.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [level, subjectTag, subjectHasMatrix]);

  useEffect(() => {
    if (!subjectHasMatrix && generationMode !== 'independent') {
      onGenerationModeChange('independent');
      onCurriculumTargetChange(null);
    }
  }, [subjectHasMatrix, generationMode, onGenerationModeChange, onCurriculumTargetChange]);

  useEffect(() => {
    if (!subjectHasMatrix) return;
    if (lockCurricular && generationMode !== 'curricular') {
      onGenerationModeChange('curricular');
    }
  }, [lockCurricular, generationMode, onGenerationModeChange, subjectHasMatrix]);

  useEffect(() => {
    if (generationMode !== 'curricular' || !catalog) return;

    // No inventar el primer eje/OA del catálogo: eso bloqueaba la sugerencia
    // por prompt/blueprint. Solo completar OAH/OAA cuando ya hay eje+OA válidos.
    const existingIsValid =
      Boolean(curriculumTarget?.axis) &&
      Boolean(curriculumTarget?.oaCode) &&
      catalog.axes.some(
        (axis) =>
          axis.id === curriculumTarget!.axis &&
          axis.oas.some((oa) => oa.code === curriculumTarget!.oaCode),
      );
    if (!existingIsValid) return;
    if (curriculumTarget?.oahCode && curriculumTarget?.oaaCode) return;

    const axis = curriculumTarget!.axis;
    const preferredOahGroup = AXIS_TO_OAH_GROUP[axis];
    const suggestedOah =
      catalog.oahs.find((oah) => oah.group === preferredOahGroup)?.code ?? catalog.oahs[0]?.code;
    const preferredOaaLetter = AXIS_TO_OAA_CODE[axis];
    const suggestedOaa =
      catalog.oaas.find((oaa) => {
        if (!preferredOaaLetter) return false;
        const code = oaa.code || '';
        return (
          code === preferredOaaLetter ||
          code === `OAA ${preferredOaaLetter}` ||
          code.toUpperCase().endsWith(preferredOaaLetter)
        );
      })?.code ?? catalog.oaas[0]?.code;

    const nextOah = curriculumTarget?.oahCode ?? suggestedOah;
    const nextOaa = curriculumTarget?.oaaCode ?? suggestedOaa;
    if (!nextOah && !nextOaa) return;
    if (nextOah === curriculumTarget?.oahCode && nextOaa === curriculumTarget?.oaaCode) return;

    onCurriculumTargetChange({
      axis,
      oaCode: curriculumTarget!.oaCode,
      oahCode: nextOah,
      oaaCode: nextOaa,
    });
  }, [generationMode, catalog, level, curriculumTarget, onCurriculumTargetChange]);

  const selectedAxis = curriculumTarget?.axis ?? '';
  const axisMeta = useMemo(
    () => catalog?.axes.find((axis) => axis.id === selectedAxis),
    [catalog, selectedAxis],
  );
  const axisOas = axisMeta?.oas ?? [];

  const selectedOa = curriculumTarget?.oaCode ?? '';
  const selectedOaMeta = axisOas.find((oa) => oa.code === selectedOa);
  const selectedOahMeta = catalog?.oahs.find((oah) => oah.code === curriculumTarget?.oahCode);
  const selectedOaaMeta = catalog?.oaas.find((oaa) => oaa.code === curriculumTarget?.oaaCode);

  useEffect(() => {
    if (!onTraceChange) return;
    if (generationMode !== 'curricular' || !curriculumTarget?.axis || !curriculumTarget.oaCode) {
      onTraceChange(null);
      return;
    }
    onTraceChange({
      axis: curriculumTarget.axis,
      axisLabel: axisMeta?.label ?? curriculumTarget.axis,
      oaCode: curriculumTarget.oaCode,
      oaText: selectedOaMeta?.text ?? '',
      oaSummary: selectedOaMeta?.summary ?? '',
      oahCode: curriculumTarget.oahCode,
      oahText: selectedOahMeta?.text,
      oaaCode: curriculumTarget.oaaCode,
      oaaText: selectedOaaMeta?.text,
    });
  }, [
    generationMode,
    curriculumTarget,
    axisMeta?.label,
    selectedOaMeta,
    selectedOahMeta,
    selectedOaaMeta,
    onTraceChange,
  ]);

  const updateTarget = (patch: Partial<CurriculumTarget>) => {
    if (!catalog) return;
    onUserEdit?.();
    const axis = patch.axis ?? curriculumTarget?.axis ?? '';
    const oas = catalog.axes.find((item) => item.id === axis)?.oas ?? [];
    const oaCode = patch.oaCode ?? curriculumTarget?.oaCode ?? oas[0]?.code ?? '';
    if (!axis || !oaCode) {
      onCurriculumTargetChange(null);
      return;
    }

    const axisChanged = patch.axis != null && patch.axis !== curriculumTarget?.axis;
    const oaChanged = patch.oaCode != null && patch.oaCode !== curriculumTarget?.oaCode;
    const resetDimensions = axisChanged || oaChanged;

    const preferredOahGroup = AXIS_TO_OAH_GROUP[axis];
    const suggestedOah =
      catalog.oahs.find((oah) => oah.group === preferredOahGroup)?.code ?? catalog.oahs[0]?.code;
    const preferredOaaLetter = AXIS_TO_OAA_CODE[axis];
    const suggestedOaa =
      catalog.oaas.find((oaa) => {
        if (!preferredOaaLetter) return false;
        const code = oaa.code || '';
        return (
          code === preferredOaaLetter ||
          code === `OAA ${preferredOaaLetter}` ||
          code.toUpperCase().endsWith(preferredOaaLetter)
        );
      })?.code ?? catalog.oaas[0]?.code;

    onCurriculumTargetChange({
      axis,
      oaCode,
      oahCode: resetDimensions
        ? patch.oahCode ?? suggestedOah
        : patch.oahCode ?? curriculumTarget?.oahCode,
      oaaCode: resetDimensions
        ? patch.oaaCode ?? suggestedOaa
        : patch.oaaCode ?? curriculumTarget?.oaaCode,
    });
  };

  const sortedOahs = useMemo(() => {
    const preferredGroup = AXIS_TO_OAH_GROUP[selectedAxis];
    if (!preferredGroup) return catalog?.oahs ?? [];
    return [...(catalog?.oahs ?? [])].sort((a, b) => {
      const aMatch = a.group === preferredGroup ? 0 : 1;
      const bMatch = b.group === preferredGroup ? 0 : 1;
      return aMatch - bMatch;
    });
  }, [catalog?.oahs, selectedAxis]);

  const suggestedOahCode = AXIS_TO_OAH_GROUP[selectedAxis]
    ? catalog?.oahs.find((oah) => oah.group === AXIS_TO_OAH_GROUP[selectedAxis])?.code
    : undefined;
  const suggestedOaaCode = AXIS_TO_OAA_CODE[selectedAxis];

  const isPlanning = variant === 'planning';
  const isWizard = appearance === 'wizard';

  if (!subjectHasMatrix) {
    return (
      <p
        className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-950"
        role="status"
      >
        {curriculumUnavailableMessage(subjectTag)}. Usa modo libre hasta que exista matriz oficial.
      </p>
    );
  }

  return (
    <div className={isWizard ? 'space-y-5' : 'space-y-4'}>
      {!isWizard ? (
        <div>
          <p className="sala-label text-slate-700">Marco curricular</p>
          <p className="text-xs text-slate-500">
            {isPlanning
              ? 'Selecciona el OA, OAH y OAA del MINEDUC que enmarcan esta ruta. La IA distribuirá las tres dimensiones entre los hitos.'
              : 'Selecciona el OA oficial del MINEDUC que enmarca esta guía. La intención concreta la describes en el paso siguiente.'}
          </p>
        </div>
      ) : null}

      {isWizard && !hideModeToggle ? (
        <div className="flex rounded-lg bg-gray-100 p-1">
          <button
            type="button"
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-xs font-medium transition',
              generationMode === 'curricular'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700',
            )}
            onClick={() => onGenerationModeChange('curricular')}
          >
            {isPlanning ? 'Planificación curricular' : 'Guía curricular'}
          </button>
          <button
            type="button"
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-xs font-medium transition',
              generationMode === 'independent'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700',
            )}
            onClick={() => {
              onUserEdit?.();
              onGenerationModeChange('independent');
              onCurriculumTargetChange(null);
            }}
          >
            {isPlanning ? 'Planificación libre' : 'Guía libre'}
          </button>
        </div>
      ) : !hideModeToggle ? (
        <div className="studio-tabs flex-wrap">
          <button
            type="button"
            className={`studio-tabs__btn ${generationMode === 'curricular' ? 'studio-tabs__btn--active' : ''}`}
            onClick={() => onGenerationModeChange('curricular')}
          >
            {isPlanning ? 'Planificación curricular' : 'Guía curricular'}
          </button>
          <button
            type="button"
            className={`studio-tabs__btn ${generationMode === 'independent' ? 'studio-tabs__btn--active' : ''}`}
            onClick={() => {
              onUserEdit?.();
              onGenerationModeChange('independent');
              onCurriculumTargetChange(null);
            }}
          >
            {isPlanning ? 'Planificación libre' : 'Guía libre'}
          </button>
        </div>
      ) : null}

      {generationMode === 'independent' && !lockCurricular ? (
        <p className={isWizard ? 'text-xs leading-relaxed text-gray-500' : 'text-xs text-slate-500'}>
          Sin escudo MINEDUC. La IA seguirá tu brief y las restricciones del nivel escolar.
        </p>
      ) : (
        <>
          {catalogLoading && (
            <p className={isWizard ? 'text-xs text-gray-400' : 'text-xs text-slate-500'}>
              Cargando matriz {catalog?.levelLabel ?? '…'}
            </p>
          )}
          {catalogError && <p className="text-xs text-amber-700">{catalogError}</p>}

          {catalog && (
            <div className="flex flex-col gap-4">
              <SelectField
                appearance={appearance}
                label="Eje temático"
                hint="Agrupa los OA del nivel"
                value={selectedAxis}
                placeholder="Selecciona un eje…"
                onChange={(axis) => {
                  const firstOa = catalog.axes.find((item) => item.id === axis)?.oas[0]?.code ?? '';
                  updateTarget({ axis, oaCode: firstOa, oahCode: undefined, oaaCode: undefined });
                }}
                options={catalog.axes.map((axis) => ({ value: axis.id, label: axis.label }))}
              />
              <SelectField
                appearance={appearance}
                label="Objetivo de Aprendizaje (OA)"
                hint={
                  isPlanning
                    ? 'Obligatorio — define el contenido de la ruta'
                    : 'Obligatorio — define el marco legal de la guía'
                }
                value={selectedOa}
                placeholder={selectedAxis ? 'Selecciona un OA…' : 'Primero elige un eje…'}
                disabled={!selectedAxis}
                onChange={(oaCode) => updateTarget({ oaCode, oahCode: undefined, oaaCode: undefined })}
                options={axisOas.map((oa) => ({
                  value: oa.code,
                  label: `${oa.code} — ${oa.summary}`,
                }))}
              />
              <SelectField
                appearance={appearance}
                label="Habilidad (OAH)"
                hint={
                  isPlanning
                    ? 'Obligatorio — habilidad que debe activarse en los hitos'
                    : 'Obligatorio — elige la habilidad que trabajará esta guía'
                }
                value={curriculumTarget?.oahCode ?? ''}
                placeholder="Selecciona una habilidad (OAH)…"
                onChange={(oahCode) => updateTarget({ oahCode: oahCode || undefined })}
                options={sortedOahs.map((oah) => ({
                  value: oah.code,
                  label: `${oah.code}${oah.code === suggestedOahCode ? ' ★' : ''} — ${oah.text.replace(/^OAH\s*[A-Za-z]:\s*/i, '').slice(0, 72)}`,
                }))}
                disabled={!sortedOahs.length}
              />
              <SelectField
                appearance={appearance}
                label="Actitud (OAA)"
                hint={
                  isPlanning
                    ? 'Obligatorio — actitud observable en la evidencia de avance'
                    : 'Obligatorio — elige la actitud observable del currículum'
                }
                value={curriculumTarget?.oaaCode ?? ''}
                placeholder="Selecciona una actitud (OAA)…"
                onChange={(oaaCode) => updateTarget({ oaaCode: oaaCode || undefined })}
                options={catalog.oaas.map((oaa) => ({
                  value: oaa.code,
                  label: `${oaa.code}${oaa.code === suggestedOaaCode ? ' ★' : ''} — ${oaa.text.replace(/^OAA\s*[A-Za-z]:\s*/i, '').slice(0, 72)}`,
                }))}
                disabled={!catalog.oaas.length}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
